import React, { useState, useEffect } from 'react';
import { useMoney } from "../../../hooks/useMoney";
import { Download, Loader2, Upload } from 'lucide-react';
import CustomModal from '../../common/CustomModal';
import { useAppDispatch } from '../../../store/hook';
import {
  fetchPrincipalWalletTransaction,
  fetchStoredBankStatements,
} from '../../../features/principal/PrincipalSlice';
import {
  WalletTransaction,
  WalletTransactionsResponse,
  PrincipalBankStatementRecord,
  ReconciliationStatus,
  WalletTxnEventType,
  WalletTxnReconciliationStatus,
} from '../../../features/principal/types';
import { Bank } from '../../../features/program/ProgramSlice';
import BankStatementUploadModal from './BankStatementUploadModal';

type Tab = 'wallet' | 'bank';

interface StatementsModalProps {
  show: boolean;
  onHide: () => void;
  principalId: string;
  principalName: string;
  iciciPrincipalBankId: number | null;
  principalBanks: Bank[] | null;
}

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const monthBackInput = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toApiDate = (v: string) => {
  const [y, m, d] = v.split('-');
  return `${d}-${m}-${y}`;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateOnly = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const csvEscape = (v: string) =>
  v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;

const reconStatusBadgeClasses: Record<ReconciliationStatus, string> = {
  unmatched: 'bg-yellow-100 text-yellow-800',
  matched: 'bg-green-100 text-green-800',
  partially_matched: 'bg-orange-100 text-orange-800',
  manually_matched: 'bg-blue-100 text-blue-800',
  ignored: 'bg-gray-100 text-gray-600',
  disputed: 'bg-red-100 text-red-800',
};

const reconStatusLabel = (status: ReconciliationStatus) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const eventTypeLabels: Record<WalletTxnEventType, string> = {
  issuance: 'Issuance',
  revoke: 'Revoke',
  expiry: 'Expiry',
  manual_topup: 'Manual Top-up',
  other: 'Other',
};

const walletReconStatusBadgeClasses: Record<WalletTxnReconciliationStatus, string> = {
  not_applicable: 'bg-gray-100 text-gray-600',
  unmatched: 'bg-yellow-100 text-yellow-800',
  matched: 'bg-green-100 text-green-800',
  partially_matched: 'bg-orange-100 text-orange-800',
  manually_matched: 'bg-blue-100 text-blue-800',
  ignored: 'bg-gray-100 text-gray-600',
  disputed: 'bg-red-100 text-red-800',
};

const walletReconStatusLabel = (status: WalletTxnReconciliationStatus) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const StatementsModal: React.FC<StatementsModalProps> = ({
  show,
  onHide,
  principalId,
  principalName,
  iciciPrincipalBankId,
  principalBanks,
}) => {
  const dispatch = useAppDispatch();
  const { symbol, locale } = useMoney();
  const formatAmount = (amount: string | null) => {
    if (!amount) return `${symbol}0`;
    return `${symbol}${parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const [activeTab, setActiveTab] = useState<Tab>('wallet');

  // Wallet tab state
  const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [walletFromDate, setWalletFromDate] = useState(monthBackInput());
  const [walletToDate, setWalletToDate] = useState(todayInput());
  const [walletDateError, setWalletDateError] = useState<string | null>(null);
  const [walletSummaryGroupBy, setWalletSummaryGroupBy] = useState<'day' | 'month' | null>(null);

  // Bank tab state
  const [bankFromDate, setBankFromDate] = useState(monthBackInput());
  const [bankToDate, setBankToDate] = useState(todayInput());
  const [bankRecords, setBankRecords] = useState<PrincipalBankStatementRecord[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [bankDateError, setBankDateError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Reset to wallet tab when modal opens
  useEffect(() => {
    if (show) {
      setActiveTab('wallet');
    }
  }, [show]);

  const bankNameById = (principalBankId: number) => {
    const match = principalBanks?.find((bank) => bank.bankId === principalBankId);
    return match?.displayName || match?.name || `Bank #${principalBankId}`;
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
  };

  const handleFetchWalletTransactions = async () => {
    if (!walletFromDate || !walletToDate) {
      setWalletDateError('Select from and to date.');
      return;
    }
    if (walletFromDate > walletToDate) {
      setWalletDateError('From date cannot be after to date.');
      return;
    }
    setWalletDateError(null);
    setWalletLoading(true);
    setWalletError(null);
    const response = await dispatch(
      fetchPrincipalWalletTransaction({
        principalId,
        limit: 500,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
        fromDate: toApiDate(walletFromDate),
        toDate: toApiDate(walletToDate),
      })
    );
    if (fetchPrincipalWalletTransaction.fulfilled.match(response)) {
      setWalletTransactions((response.payload as WalletTransactionsResponse).transactions ?? []);
    } else {
      setWalletError((response.payload as string) || 'Failed to load transactions');
    }
    setWalletLoading(false);
  };

  const handleFetchBankStatement = async () => {
    if (!bankFromDate || !bankToDate) {
      setBankDateError('Select from and to date.');
      return;
    }
    if (bankFromDate > bankToDate) {
      setBankDateError('From date cannot be after to date.');
      return;
    }
    setBankDateError(null);
    setBankLoading(true);
    setBankError(null);

    const response = await dispatch(
      fetchStoredBankStatements({
        principalId,
        principalBankId: iciciPrincipalBankId,
        fromDate: toApiDate(bankFromDate),
        toDate: toApiDate(bankToDate),
        limit: 500,
      })
    );

    if (fetchStoredBankStatements.fulfilled.match(response)) {
      setBankRecords(response.payload.results ?? []);
    } else {
      setBankError((response.payload as string) || 'Failed to fetch bank statements');
      setBankRecords([]);
    }
    setBankLoading(false);
  };

  const walletSummaryRows = React.useMemo(() => {
    if (!walletSummaryGroupBy || !walletTransactions.length) return [];

    const groups = new Map<
      string,
      { key: string; label: string; debitTotal: number; creditTotal: number; netAmount: number; count: number }
    >();

    for (const t of walletTransactions) {
      const isCredit = String(t.transactionTypeDisplay).toLowerCase() === 'credit';
      const dateString = isCredit ? t.createdAt : t.debitDate || t.createdAt;
      const date = dateString ? new Date(dateString) : null;
      if (!date || Number.isNaN(date.getTime())) continue;

      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      const key = walletSummaryGroupBy === 'month'
        ? `${year}-${String(month + 1).padStart(2, '0')}`
        : `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      const label = walletSummaryGroupBy === 'month'
        ? date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

      const amount = parseFloat(t.amount) || 0;

      if (!groups.has(key)) {
        groups.set(key, { key, label, debitTotal: 0, creditTotal: 0, netAmount: 0, count: 0 });
      }
      const row = groups.get(key)!;
      if (isCredit) {
        row.creditTotal += amount;
        row.netAmount += amount;
      } else {
        row.debitTotal += amount;
        row.netAmount -= amount;
      }
      row.count += 1;
    }

    return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [walletTransactions, walletSummaryGroupBy]);

  const formatAmountNumber = (amount: number) =>
    `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const downloadWalletTransactions = () => {
    if (!walletTransactions.length) return;
    const headers = ['Date', 'Type', 'Event', 'Voucher', 'Amount', 'Program', 'Bank Ref / Remark', 'Recon Status'];
    const rows = walletTransactions.map((t) => {
      const isCredit = String(t.transactionTypeDisplay).toLowerCase() === 'credit';
      const date = isCredit ? t.createdAt : t.debitDate || t.createdAt;
      const formattedDate = date ? new Date(date).toISOString().split('T')[0] : '';
      return [
        formattedDate,
        t.transactionTypeDisplay?.toUpperCase() ?? '',
        t.eventType ? eventTypeLabels[t.eventType] : '',
        t.saralId ?? '',
        t.amount ?? '',
        t.wallet?.program?.programName ?? '',
        t.remark ?? '',
        t.reconciliationStatus ? walletReconStatusLabel(t.reconciliationStatus) : '',
      ].map(csvEscape).join(',');
    });
    const csv = [headers.map(csvEscape).join(','), ...rows].join('\n');
    triggerDownload(csv, `${principalName}_wallet_transactions_${todayInput()}.csv`);
  };

  const downloadBankStatement = () => {
    if (!bankRecords.length) return;
    const headers = [
      'Transaction ID',
      'Cheque No',
      'Txn Date',
      'Remarks',
      'Amount',
      'Balance',
      'Value Date',
      'Type',
      'Recon Status',
    ];
    const rows = bankRecords.map((row) =>
      [
        row.transactionId ?? '',
        row.chequeNo ?? '',
        row.txnDate ?? '',
        row.remarks ?? '',
        row.amount ?? '',
        row.balance ?? '',
        row.valueDate ?? '',
        row.txnType ?? '',
        row.reconciliationStatus ?? '',
      ]
        .map((v) => csvEscape(String(v)))
        .join(',')
    );
    const csv = [headers.map(csvEscape).join(','), ...rows].join('\n');
    triggerDownload(csv, `bank_statement_${bankFromDate}_${bankToDate}.csv`);
  };

  const triggerDownload = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <CustomModal show={show} onHide={onHide} title="Statements" width="w-[95vw] max-w-[1200px]">
      <div className="w-full flex flex-col">
        {/* Tab navigation */}
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          <button
            onClick={() => handleTabChange('wallet')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'wallet'
                ? 'text-brand border-brand'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Wallet Statements
          </button>
          <button
            onClick={() => handleTabChange('bank')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'bank'
                ? 'text-brand border-brand'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Bank Statements
          </button>
        </div>

        {/* Wallet Statements */}
        {activeTab === 'wallet' && (
          <div className="flex flex-col">
            <div className="flex items-end gap-3 flex-wrap mb-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">From Date</label>
                <input
                  type="date"
                  value={walletFromDate}
                  onChange={(e) => setWalletFromDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">To Date</label>
                <input
                  type="date"
                  value={walletToDate}
                  onChange={(e) => setWalletToDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={handleFetchWalletTransactions}
                disabled={walletLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 inline-flex items-center gap-2 disabled:opacity-50"
              >
                {walletLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Fetch
              </button>
              <button
                onClick={downloadWalletTransactions}
                disabled={!walletTransactions.length}
                className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
            {walletDateError && <div className="text-sm text-red-600 mb-3">{walletDateError}</div>}
            {walletError && <div className="text-sm text-red-600 mb-3">{walletError}</div>}

            {walletTransactions.length > 0 && (
              <div className="flex items-center justify-between mb-3">
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setWalletSummaryGroupBy(null)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      walletSummaryGroupBy === null ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Detailed
                  </button>
                  <button
                    type="button"
                    onClick={() => setWalletSummaryGroupBy('day')}
                    className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                      walletSummaryGroupBy === 'day' ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Daily Total
                  </button>
                  <button
                    type="button"
                    onClick={() => setWalletSummaryGroupBy('month')}
                    className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                      walletSummaryGroupBy === 'month' ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Monthly Total
                  </button>
                </div>
                <span className="text-xs text-gray-400">Based on the {walletTransactions.length} transaction(s) fetched for this range</span>
              </div>
            )}

            {walletSummaryGroupBy && walletSummaryRows.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '260px' }}>
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2.5 px-4 font-semibold text-gray-700">
                          {walletSummaryGroupBy === 'month' ? 'Month' : 'Date'}
                        </th>
                        <th className="text-right py-2.5 px-4 font-semibold text-gray-700">Total Debit</th>
                        <th className="text-right py-2.5 px-4 font-semibold text-gray-700">Total Credit</th>
                        <th className="text-right py-2.5 px-4 font-semibold text-gray-700">Net</th>
                        <th className="text-right py-2.5 px-4 font-semibold text-gray-700">Txns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletSummaryRows.map((row, index) => (
                        <tr
                          key={row.key}
                          className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        >
                          <td className="py-2 px-4 text-gray-700">{row.label}</td>
                          <td className="py-2 px-4 text-right text-red-600">
                            {row.debitTotal > 0 ? `-${formatAmountNumber(row.debitTotal)}` : '-'}
                          </td>
                          <td className="py-2 px-4 text-right text-green-600">
                            {row.creditTotal > 0 ? `+${formatAmountNumber(row.creditTotal)}` : '-'}
                          </td>
                          <td className={`py-2 px-4 text-right font-medium ${row.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {row.netAmount >= 0 ? '+' : '-'}{formatAmountNumber(Math.abs(row.netAmount))}
                          </td>
                          <td className="py-2 px-4 text-right text-gray-600">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="max-h-[520px] overflow-y-auto overflow-x-auto">
                {!walletLoading && walletTransactions.length === 0 && (
                  <div className="py-10 text-center text-gray-500">
                    Select a date range and click Fetch.
                  </div>
                )}
                {walletTransactions.length > 0 && !walletSummaryGroupBy && (
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Event</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Voucher</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Program</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Bank Ref / Remark</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Recon Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletTransactions.map((t, idx) => {
                        const isCredit = String(t.transactionTypeDisplay).toLowerCase() === 'credit';
                        const date = isCredit ? t.createdAt : t.debitDate || t.createdAt;
                        const reconStatus = t.reconciliationStatus || 'not_applicable';
                        return (
                          <tr
                            key={t.transactionId}
                            className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                          >
                            <td className="py-3 px-4 text-gray-700">{formatDate(date)}</td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  isCredit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {t.transactionTypeDisplay}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {t.eventType ? eventTypeLabels[t.eventType] : '-'}
                            </td>
                            <td className="py-3 px-4 text-gray-600">{t.saralId || '-'}</td>
                            <td
                              className={`py-3 px-4 text-right font-medium ${
                                isCredit ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {isCredit ? '+' : '-'}
                              {formatAmount(t.amount)}
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {t.wallet?.program?.programName ?? '-'}
                            </td>
                            <td className="py-3 px-4 text-gray-600">{t.remark || '-'}</td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  walletReconStatusBadgeClasses[reconStatus] || 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {walletReconStatusLabel(reconStatus)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bank Statement */}
        {activeTab === 'bank' && (
          <div className="flex flex-col">
            <div className="flex items-end gap-3 flex-wrap mb-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">From Date</label>
                <input
                  type="date"
                  value={bankFromDate}
                  onChange={(e) => setBankFromDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">To Date</label>
                <input
                  type="date"
                  value={bankToDate}
                  onChange={(e) => setBankToDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={handleFetchBankStatement}
                disabled={bankLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 inline-flex items-center gap-2 disabled:opacity-50"
              >
                {bankLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Fetch
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Statement
              </button>
              <button
                onClick={downloadBankStatement}
                disabled={!bankRecords.length}
                className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
            {bankDateError && <div className="text-sm text-red-600 mb-3">{bankDateError}</div>}
            {bankError && <div className="text-sm text-red-600 mb-3">{bankError}</div>}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="max-h-[520px] overflow-y-auto">
                {!bankLoading && bankRecords.length === 0 && (
                  <div className="py-10 text-center text-gray-500">
                    Select a date range and click Fetch.
                  </div>
                )}
                {bankRecords.length > 0 && (
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Date</th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Bank</th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Remarks</th>
                        <th className="text-right py-3 px-3 font-semibold text-gray-700">Amount</th>
                        <th className="text-right py-3 px-3 font-semibold text-gray-700">Balance</th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Value Date</th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Type</th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Transaction ID</th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-700">Recon Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bankRecords.map((row) => {
                        const isCredit = row.txnType === 'CR';
                        return (
                          <tr
                            key={row.principalBankStatementId}
                            className="border-b border-gray-100 align-top"
                          >
                            <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                              {formatDate(row.txnDate)}
                            </td>
                            <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                              {bankNameById(row.principalBankId)}
                            </td>
                            <td className="py-3 px-3 text-gray-700 whitespace-pre-wrap break-words">
                              {row.remarks || '-'}
                            </td>
                            <td
                              className={`py-3 px-3 text-right font-medium ${
                                isCredit ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {formatAmount(row.amount)}
                            </td>
                            <td className="py-3 px-3 text-right text-gray-700">
                              {formatAmount(row.balance)}
                            </td>
                            <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                              {formatDateOnly(row.valueDate)}
                            </td>
                            <td className="py-3 px-3">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  isCredit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {row.txnType}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-gray-700 break-all">
                              {row.transactionId || '-'}
                            </td>
                            <td className="py-3 px-3">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  reconStatusBadgeClasses[row.reconciliationStatus] ||
                                  'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {reconStatusLabel(row.reconciliationStatus)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <BankStatementUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        principalId={principalId}
        principalBanks={principalBanks}
        defaultPrincipalBankId={iciciPrincipalBankId}
        onUploadSuccess={handleFetchBankStatement}
      />
    </CustomModal>
  );
};

export default StatementsModal;
